import { Expr } from "@redotech/redo-automations-model/expr/ast";
import {
  evaluate,
  fieldKey,
} from "@redotech/redo-automations-model/expr/evaluate";
import { collectReferences } from "@redotech/redo-automations-model/expr/references";
import {
  JsonObject,
  JsonValue,
  isJsonObject,
} from "@redotech/redo-automations-model/json";
import {
  FieldDef,
  Path,
  TypeRef,
  TypeSystem,
  unwrapNamed,
} from "@redotech/redo-automations-model/types/type-system";
import { Location } from "./state";

export const RESOLVE_EFFECT = "context.resolve";

/** One lazy field to fetch: the resolver gets the parent object it hangs off. */
export interface ResolveRequest {
  location: Location;
  resolver: string;
  parent: JsonValue;
  args: Record<string, JsonValue>;
}

export type ResolveAnswer =
  | { ok: true; value: JsonValue }
  | { ok: false; message: string };

interface PlannedRequest extends ResolveRequest {
  fallback: JsonValue;
  failRun: boolean;
}

/**
 * Lazy fields the expressions need that the context doesn't hold yet. Only
 * the first missing level along each path is returned; nested lazy fields are
 * planned again once their parents are in place (resolution runs in rounds).
 */
export function planResolution(
  ts: TypeSystem,
  root: TypeRef,
  context: JsonObject,
  exprs: Expr[],
  now: string,
): PlannedRequest[] {
  const out: PlannedRequest[] = [];
  const seen = new Set<string>();
  const plan = (expr: Expr) => {
    for (const path of collectReferences(expr)) {
      walk(ts, root, context, path, 0, [], out, seen);
    }
  };
  for (const expr of exprs) {
    planShortCircuit(expr, context, now, plan, () => out.length);
  }
  return out;
}

/**
 * and/or are planned one argument at a time: an argument's lookups are only
 * requested once every earlier argument is known not to decide the result,
 * so a decisive early condition skips later (possibly expensive) lookups.
 */
function planShortCircuit(
  expr: Expr,
  context: JsonObject,
  now: string,
  plan: (e: Expr) => void,
  planned: () => number,
): void {
  if (expr.kind !== "call" || (expr.fn !== "and" && expr.fn !== "or")) {
    plan(expr);
    return;
  }
  for (const arg of expr.args) {
    const before = planned();
    planShortCircuit(arg, context, now, plan, planned);
    if (planned() > before) {
      return;
    }
    let value: JsonValue;
    try {
      value = evaluate(arg, { data: context, now });
    } catch {
      return;
    }
    if (expr.fn === "and" ? value !== true : value === true) {
      return;
    }
  }
}

function walk(
  ts: TypeSystem,
  type: TypeRef,
  value: JsonValue | undefined,
  path: Path,
  index: number,
  location: Location,
  out: PlannedRequest[],
  seen: Set<string>,
): void {
  if (index >= path.length || value === undefined || value === null) {
    return;
  }
  if (Array.isArray(value)) {
    const element = type.kind === "list" ? type.of : type;
    for (const [i, item] of value.entries()) {
      walk(ts, element, item, path, index, [...location, i], out, seen);
    }
    return;
  }
  if (!isJsonObject(value)) {
    return;
  }
  const segment = path[index];
  if (!segment) {
    return;
  }
  const named = ts.types[unwrapNamed(type).name];
  if (!named || named.kind !== "object") {
    return;
  }
  const field: FieldDef | undefined = named.fields[segment.field];
  if (!field) {
    return;
  }
  const key = fieldKey(segment);
  const childLocation = [...location, key];
  if (field.resolver && !(key in value)) {
    const id = JSON.stringify(childLocation);
    if (!seen.has(id)) {
      seen.add(id);
      out.push({
        location: childLocation,
        resolver: field.resolver,
        parent: stripLazy(value),
        args: segment.args ?? {},
        fallback: field.fallback ?? null,
        failRun: field.onFailure === "fail-run",
      });
    }
    return;
  }
  walk(ts, field.type, value[key], path, index + 1, childLocation, out, seen);
}

/** Resolvers get the parent's plain data; previously fetched siblings are left out. */
function stripLazy(value: JsonObject): JsonObject {
  const out: JsonObject = {};
  for (const [k, v] of Object.entries(value)) {
    if (!k.includes("(")) {
      out[k] = v;
    }
  }
  return out;
}

export function writeAt(
  root: JsonObject,
  location: Location,
  value: JsonValue,
): JsonObject {
  const [head, ...rest] = location;
  if (head === undefined) {
    return root;
  }
  if (typeof head === "number") {
    throw new Error("Context root is an object");
  }
  const next = { ...root };
  next[head] = rest.length === 0 ? value : writeInto(root[head], rest, value);
  return next;
}

function writeInto(
  current: JsonValue | undefined,
  location: Location,
  value: JsonValue,
): JsonValue {
  const [head, ...rest] = location;
  if (head === undefined) {
    return value;
  }
  if (typeof head === "number") {
    const list = Array.isArray(current) ? [...current] : [];
    list[head] = rest.length === 0 ? value : writeInto(list[head], rest, value);
    return list;
  }
  const obj: JsonObject = isJsonObject(current) ? { ...current } : {};
  obj[head] = rest.length === 0 ? value : writeInto(obj[head], rest, value);
  return obj;
}

export function deleteAt(root: JsonObject, location: Location): JsonObject {
  const [head, ...rest] = location;
  if (head === undefined || typeof head === "number") {
    return root;
  }
  const next = { ...root };
  if (rest.length === 0) {
    delete next[head];
  } else {
    const child = deleteInto(root[head], rest);
    if (child !== undefined) {
      next[head] = child;
    }
  }
  return next;
}

function deleteInto(
  current: JsonValue | undefined,
  location: Location,
): JsonValue | undefined {
  const [head, ...rest] = location;
  if (head === undefined || current === undefined) {
    return current;
  }
  if (typeof head === "number") {
    if (!Array.isArray(current)) {
      return current;
    }
    const list = [...current];
    const child = list[head];
    if (rest.length === 0 || child === undefined) {
      return list;
    }
    const updated = deleteInto(child, rest);
    if (updated !== undefined) {
      list[head] = updated;
    }
    return list;
  }
  if (!isJsonObject(current)) {
    return current;
  }
  const obj = { ...current };
  if (rest.length === 0) {
    delete obj[head];
  } else {
    const updated = deleteInto(obj[head], rest);
    if (updated !== undefined) {
      obj[head] = updated;
    }
  }
  return obj;
}
